import { upload as vercelUpload } from "@vercel/blob/client";
import { UImage } from "@/app/utils/UImage";
import { Globals } from "@/app/vars/Globals";
import { ULogger } from "./ULogger";

export class UFileStorage {
  private static env(): string {
    return Globals.IS_PROD_ENV ? "production" : "development";
  }

  public static getUserAvatarPath(uuid: string, fileName: string): string {
    return `${UFileStorage.env()}/${uuid}/${UImage.AVATAR_FOLDER}/${fileName}`;
  }

  private static readonly COMPANY_FOLDER = "companies";

  public static getCompanyLogoPath(key: string, fileName: string): string {
    return `${UFileStorage.env()}/${UFileStorage.COMPANY_FOLDER}/${key}/${UImage.LOGO_FOLDER}/${fileName}`;
  }

  // The company id embedded in a company-logo upload path, or null when the path is not one.
  // Lets the upload route authorize a company manager to replace their company's logo.
  public static getCompanyIdFromLogoPath(pathname: string): number | null {
    const match = pathname.match(
      new RegExp(`/${UFileStorage.COMPANY_FOLDER}/(\\d+)/${UImage.LOGO_FOLDER}/`),
    );
    return match ? parseInt(match[1], 10) : null;
  }

  public static getUserCallPath(uuid: string, fileName: string): string {
    return `${UFileStorage.env()}/${uuid}/calls/${fileName}`;
  }

  public static getUserCallExportsPath(uuid: string, fileName: string): string {
    return `${UFileStorage.getUserCallPath(uuid, "")}exports/${fileName}`;
  }

  public static getProjectScreenshotPath(projectUuid: string, fileName: string): string {
    return `${UFileStorage.env()}/projects/${projectUuid}/screenshots/${fileName}`;
  }

  public static getClientAvatarPath(projectUuid: string, fileName: string): string {
    return `${UFileStorage.env()}/projects/${projectUuid}/client-avatars/${fileName}`;
  }

  public static getClientAvatarExportsPath(projectUuid: string, fileName: string): string {
    return `${UFileStorage.getClientAvatarPath(projectUuid, "")}exports/${fileName}`;
  }

  public static getProjectDemoPath(projectUuid: string, fileName: string): string {
    return `${UFileStorage.env()}/projects/${projectUuid}/demo/${fileName}`;
  }

  public static async upload(file: File, pathname: string): Promise<string | null> {
    try {
      if (Globals.IS_PROD_ENV) {
        const blob = await vercelUpload(pathname, file, {
          access: "public",
          handleUploadUrl: "/api/upload",
        });
        return blob.url;
      } else {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("pathname", pathname);

        const response = await fetch("/api/upload", {
          method: "POST",
          body: formData,
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || "Upload failed");
        }

        const data = await response.json();
        return data.url;
      }
    } catch (error) {
      ULogger.error(UFileStorage.name, "UFileStorage.upload error:", error);
      return null;
    }
  }
}
